import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CardTabs,
  MaterialSymbol,
  PartnerOnboardingApplication,
  PartnerOnboardingApplicationDocument,
  PartnerOnboardingApplicationForm,
  PartnerOnboardingCollaborator,
  PartnerOnboardingIDVerification,
  ProgressBar,
  parseUTCDateTime,
  withStyles,
} from 'vopay';
import { BodyText, Card, Title } from 'UI';
import useOnboardingTemplateName from '@/hooks/useOnboardingTemplateName';
import formatShortDate from '@/components/OnboardingTracker/formatShortDate';
import CollaboratorCard from './CollaboratorCard';
import IDVerificationCard from './IDVerificationCard';
import InProgressIcon from './svg/InProgress.svg';
import NotStartedIcon from './svg/NotStarted.svg';
import styles from './ApplicationProgress.module.scss';

const cn = withStyles(styles);

export type ApplicationProgressProps = {
  /**
   * The drawer's application. The drawer renders this component only when there is an application
   * (`Forms` and `Documents` are non-null); it covers the Application Progress card and the
   * Details / Forms / Documents / Collaborators tabs (plan steps 6–7; ID-14064).
   */
  application: PartnerOnboardingApplication;
  /** Not an Admin (i.e. a Viewer): the ID verification copy and Resend buttons and collaborator Resend are disabled. */
  isReadOnly: boolean;
  /**
   * Another drawer action is in flight: ID verification Resend and collaborator Resend are disabled (collaborator
   * Resend is also disabled on a link that isn't Active).
   */
  isActionPending: boolean;
  onCopyIDVerificationLinkClick: (link: string) => void;
  onResendIDVerificationClick: (individual: PartnerOnboardingIDVerification) => void;
  onResendCollaboratorClick: (collaborator: PartnerOnboardingCollaborator) => void;
};

type ProgressTab = 'details' | 'forms' | 'documents' | 'collaborators';

/** A row's look (Figma "Progress Tracking" frames): icon and name colour. */
type RowState = 'done' | 'inProgress' | 'idle';

const NO_VALUE = '—';

function isProgressTab(value: string): value is ProgressTab {
  return value === 'details' || value === 'forms' || value === 'documents' || value === 'collaborators';
}

function getFormRowState({ Status }: PartnerOnboardingApplicationForm): RowState {
  if (Status === 'Completed') return 'done';
  if (Status === 'InProgress') return 'inProgress';

  return 'idle';
}

function getDocumentRowState({ Status }: PartnerOnboardingApplicationDocument): RowState {
  // Figma has no Skipped row; a skipped document isn't uploaded, so it takes the Not Started look (grey
  // icon, default name colour) rather than the green check — judgement call (plan step 6).
  return Status === 'Uploaded' ? 'done' : 'idle';
}

function StatusIcon({ state }: { state: RowState }) {
  if (state === 'done') {
    return <MaterialSymbol icon="check_circle" size="24px" fillAmount={1} className={cn('doneIcon')} />;
  }

  return <img src={state === 'inProgress' ? InProgressIcon : NotStartedIcon} alt="" className={cn('statusIcon')} />;
}

type ProgressRowProps = { name: string; state: RowState; statusLabel: string };

function ProgressRow({ name, state, statusLabel }: ProgressRowProps) {
  return (
    <li className={cn('row')}>
      <div className={cn('rowName')}>
        <StatusIcon state={state} />

        <BodyText weight="md" className={cn(state !== 'idle' && `name-${state}`)}>
          {name}
        </BodyText>
      </div>

      <BodyText className={cn('secondaryText', 'rowStatus')}>{statusLabel}</BodyText>
    </li>
  );
}

export default function ApplicationProgress({
  application,
  isReadOnly,
  isActionPending,
  onCopyIDVerificationLinkClick,
  onResendIDVerificationClick,
  onResendCollaboratorClick,
}: ApplicationProgressProps) {
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'OnboardingTracker.ApplicationProgress' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { resolveFormName, resolveDocumentName } = useOnboardingTemplateName();

  // Details is selected on open (spec §6.7). The selection remembers which entity it was made for, so a
  // drawer that switches entities without remounting this component starts on Details again.
  const entityKey = `${application.EntityType}:${application.EntityID}`;

  const [selectedTab, setSelectedTab] = useState<{ entityKey: string; tab: ProgressTab }>({
    entityKey,
    tab: 'details',
  });

  const activeTab = selectedTab.entityKey === entityKey ? selectedTab.tab : 'details';

  const forms = application.Forms ?? [];
  const documents = application.Documents ?? [];
  const collaborators = application.Collaborators ?? [];
  const idVerifications = application.IDVerifications ?? [];
  // `/collaborator/resend` rejects any link that isn't Active (a Submitted or Expired application), so
  // Resend is disabled rather than left to fail. Unlike Extend, this covers Expired too.
  const isCollaboratorResendDisabled = isReadOnly || isActionPending || application.LinkStatus !== 'Active';
  const numFormsCompleted = forms.filter(({ Status }) => Status === 'Completed').length;
  // Only uploads count as completed: a Skipped document (a reason instead of a file) isn't counted, to
  // match its grey Not Started look in the list. Spec §6.6 predates Skipped — judgement call.
  const numDocumentsCompleted = documents.filter(({ Status }) => Status === 'Uploaded').length;
  const progress = Math.min(100, Math.max(0, Math.round(application.Progress ?? 0)));

  function getFormStatusLabel({ Status, CompletedDate }: PartnerOnboardingApplicationForm) {
    if (Status === 'InProgress') return t('FormStatus.inProgress', { defaultValue: 'In progress' });
    if (Status === 'NotStarted') return t('FormStatus.notStarted', { defaultValue: 'Not Started' });

    // `CompletedDate` is UTC and null for forms completed before completion dates were recorded.
    const completedDate = CompletedDate ? parseUTCDateTime(CompletedDate) : null;

    return completedDate
      ? t('FormStatus.completedOn', {
          defaultValue: 'Completed on {{date}}',
          date: formatShortDate(completedDate, i18n.language),
        })
      : t('FormStatus.completed', { defaultValue: 'Completed' });
  }

  function getDocumentStatusLabel({ Status }: PartnerOnboardingApplicationDocument) {
    if (Status === 'Uploaded') return t('DocumentStatus.uploaded', { defaultValue: 'Uploaded' });
    if (Status === 'Skipped') return t('DocumentStatus.skipped', { defaultValue: 'Skipped' });

    return t('DocumentStatus.notStarted', { defaultValue: 'Not Started' });
  }

  const formsLabel = t('forms', { defaultValue: 'Forms' });
  const documentsLabel = t('documents', { defaultValue: 'Documents' });
  const collaboratorsLabel = t('collaborators', { defaultValue: 'Collaborators' });

  return (
    <div className={cn('container')}>
      <Card variant="section" className={cn('progressCard')}>
        <Title level="6" className={cn('progressTitle')}>
          {t('title', { defaultValue: 'Application Progress' })}
        </Title>

        <div className={cn('progressBarRow')}>
          <ProgressBar variant="track" className={cn('progressBar')} percentComplete={progress} />
          {/* H3/Large Title 24/Emphasized on a figure, not a heading: styled in the module SCSS. */}
          <span className={cn('progressPercent')}>{`${progress}%`}</span>
        </div>

        <div className={cn('counts')}>
          {[
            { label: formsLabel, numCompleted: numFormsCompleted, numTotal: forms.length },
            { label: documentsLabel, numCompleted: numDocumentsCompleted, numTotal: documents.length },
          ].map(({ label, numCompleted, numTotal }) => (
            <div key={label} className={cn('count')}>
              {/* Highlight/12/Emphasized has no vopay text component: styled in the module SCSS. */}
              <span className={cn('countLabel')}>{label}</span>

              <div className={cn('countValue')}>
                <BodyText weight="em">{numCompleted}</BodyText>

                <BodyText className={cn('secondaryText')}>
                  {t('ofTotalCompleted', { defaultValue: '/{{numTotal}} Completed', numTotal })}
                </BodyText>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <CardTabs.Root
        value={activeTab}
        onValueChange={(value) => {
          if (isProgressTab(value)) setSelectedTab({ entityKey, tab: value });
        }}
      >
        <CardTabs.List>
          <CardTabs.Trigger value="details">{t('details', { defaultValue: 'Details' })}</CardTabs.Trigger>
          <CardTabs.Trigger value="forms">{formsLabel}</CardTabs.Trigger>
          <CardTabs.Trigger value="documents">{documentsLabel}</CardTabs.Trigger>
          <CardTabs.Trigger value="collaborators">{collaboratorsLabel}</CardTabs.Trigger>
        </CardTabs.List>

        <CardTabs.Content value="details" className={cn('tabContent', 'stackedTabContent')}>
          <Card variant="section" className={cn('listCard')}>
            <BodyText weight="md" className={cn('secondaryText', 'listCardTitle')}>
              {t('recipient', { defaultValue: 'Recipient' })}
            </BodyText>

            <ul className={cn('list', 'recipientList')}>
              <li className={cn('row')}>
                <BodyText weight="md">{t('fullName', { defaultValue: 'Full Name' })}</BodyText>

                <BodyText className={cn('secondaryText', 'rowStatus')}>
                  {application.Recipient?.FullName ?? NO_VALUE}
                </BodyText>
              </li>

              <li className={cn('row')}>
                <BodyText weight="md">{tGlobal('email')}</BodyText>

                {/* The landed spec documents Email as nullable ("only if the onboarding app returned none"). */}
                <BodyText className={cn('secondaryText', 'rowStatus')}>
                  {application.Recipient?.Email ?? NO_VALUE}
                </BodyText>
              </li>
            </ul>
          </Card>

          {/* No card without individuals (plan, "What gets built"). */}
          {idVerifications.length > 0 && (
            <IDVerificationCard
              idVerifications={idVerifications}
              isReadOnly={isReadOnly}
              isActionPending={isActionPending}
              isOnboardingEmailAllowed={application.CanSendOnboardingEmail}
              onCopyLinkClick={onCopyIDVerificationLinkClick}
              onResendClick={onResendIDVerificationClick}
            />
          )}
        </CardTabs.Content>

        <CardTabs.Content value="forms" className={cn('tabContent')}>
          <Card variant="section" className={cn('listCard')}>
            {forms.length > 0 ? (
              <ul className={cn('list')}>
                {forms.map((form, index) => (
                  // Several forms can share an identifier (one per person), and names can repeat.

                  <ProgressRow
                    key={index}
                    name={resolveFormName(form.Identifier, form.Name)}
                    state={getFormRowState(form)}
                    statusLabel={getFormStatusLabel(form)}
                  />
                ))}
              </ul>
            ) : (
              // Figma has no empty state; the spec's `x-empty-when` allows `{}` — judgement call.
              <BodyText className={cn('secondaryText', 'emptyState')}>
                {t('noForms', { defaultValue: 'No forms in this application.' })}
              </BodyText>
            )}
          </Card>
        </CardTabs.Content>

        <CardTabs.Content value="documents" className={cn('tabContent')}>
          <Card variant="section" className={cn('listCard')}>
            {documents.length > 0 ? (
              <ul className={cn('list')}>
                {documents.map((document, index) => (
                  <ProgressRow
                    key={index}
                    name={resolveDocumentName(document.Identifier, document.Name)}
                    state={getDocumentRowState(document)}
                    statusLabel={getDocumentStatusLabel(document)}
                  />
                ))}
              </ul>
            ) : (
              // Figma has no empty state; the spec's `x-empty-when` allows `{}` — judgement call.
              <BodyText className={cn('secondaryText', 'emptyState')}>
                {t('noDocuments', { defaultValue: 'No documents in this application.' })}
              </BodyText>
            )}
          </Card>
        </CardTabs.Content>

        <CardTabs.Content value="collaborators" className={cn('tabContent', 'stackedTabContent')}>
          <div className={cn('collaboratorsHeader')}>
            <BodyText weight="md" className={cn('secondaryText')}>
              {collaboratorsLabel}
            </BodyText>

            <BodyText size="sm" className={cn('secondaryText')}>
              {t('Collaborators.numPeople', { defaultValue: '{{count}} people', count: collaborators.length })}
            </BodyText>
          </div>

          {collaborators.length > 0 ? (
            collaborators.map((collaborator) => (
              <CollaboratorCard
                key={collaborator.CollaboratorID}
                collaborator={collaborator}
                isResendDisabled={isCollaboratorResendDisabled}
                isOnboardingEmailAllowed={application.CanSendOnboardingEmail}
                onResendClick={onResendCollaboratorClick}
              />
            ))
          ) : (
            // Figma has no empty state. The populated tab has no outer card, so the empty line sits in a list
            // card under the header, to look like the Forms / Documents empty lines — judgement call.
            <Card variant="section" className={cn('listCard')}>
              <BodyText className={cn('secondaryText', 'emptyState')}>
                {t('Collaborators.empty', { defaultValue: 'No collaborators in this application.' })}
              </BodyText>
            </Card>
          )}
        </CardTabs.Content>
      </CardTabs.Root>
    </div>
  );
}
